<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div class="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" @click.stop>
      <div class="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Agendar Reconvocação</h3>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
          <XIcon class="w-5 h-5" />
        </button>
      </div>
      
      <div class="p-6">
        <!-- Informações do Paciente -->
        <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
          <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-3">Informações do Paciente</h4>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400">Nome:</p>
              <p class="font-medium text-gray-900 dark:text-white">{{ recallItem?.patient_name }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400">Documento:</p>
              <p class="font-medium text-gray-900 dark:text-white">{{ recallItem?.patient_document }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400">Exame Original:</p>
              <p class="font-medium text-gray-900 dark:text-white">{{ recallItem?.service_name }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400">OS Original:</p>
              <p class="font-medium text-blue-600 dark:text-blue-400">{{ recallItem?.accession_number }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400">Data Original:</p>
              <p class="font-medium text-gray-900 dark:text-white">{{ formatDate(recallItem?.original_date) }} às {{ recallItem?.original_time }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400">Motivo da Reconvocação:</p>
              <p class="font-medium text-gray-900 dark:text-white">{{ recallItem?.reason }}</p>
            </div>
          </div>
        </div>

        <!-- Formulário de Agendamento -->
        <form @submit.prevent="handleSchedule" class="space-y-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Data do Novo Agendamento*
              </label>
              <input 
                v-model="scheduleData.date" 
                type="date" 
                required 
                :min="minDate"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500" 
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Horário*
              </label>
              <select 
                v-model="scheduleData.time" 
                required 
                :disabled="isLoadingTimes"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="">
                  {{ isLoadingTimes ? 'Carregando horários...' : 'Selecione um horário' }}
                </option>
                <option v-for="time in availableTimes" :key="time" :value="time">{{ time }}</option>
              </select>
              <p v-if="availableTimes.length === 0 && !isLoadingTimes" class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Nenhum horário disponível para esta data.
              </p>
            </div>
          </div>



          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Observações Adicionais
            </label>
            <textarea 
              v-model="scheduleData.notes" 
              rows="3" 
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="Observações sobre o novo agendamento..."
            ></textarea>
          </div>

          <!-- Aviso sobre agendamento -->
          <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div class="flex items-start">
              <div class="flex-shrink-0">
                <InformationCircleIcon class="h-5 w-5 text-blue-400" />
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-blue-800 dark:text-blue-200">Agendamento de Reconvocação</h3>
                <div class="mt-2 text-sm text-blue-700 dark:text-blue-300">
                  <p>Este paciente será agendado para uma nova consulta. O sistema criará automaticamente um novo agendamento com as informações fornecidas. A sala será definida posteriormente pelo setor de agendamento.</p>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
      
      <div class="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
        <button 
          type="button" 
          @click="$emit('close')" 
          class="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-white rounded-lg text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-500"
        >
          Cancelar
        </button>
        <button 
          type="button" 
          @click="handleSchedule" 
          :disabled="isProcessing || !isFormValid" 
          class="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <template v-if="isProcessing">
            <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Agendando...</span>
          </template>
          <template v-else>
            <CalendarIcon class="w-4 h-4" />
            <span>Confirmar Agendamento</span>
          </template>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { XIcon, CalendarIcon, InformationCircleIcon } from '@heroicons/vue/24/outline'
import { recallQueueService } from '@/services/recallQueueService'

export default {
  name: 'ScheduleRecallModal',
  components: {
    XIcon,
    CalendarIcon,
    InformationCircleIcon
  },
  props: {
    recallItem: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'scheduled'],
  setup(props, { emit }) {
    const isProcessing = ref(false)
    const availableTimes = ref([])
    const isLoadingTimes = ref(false)
    
    const scheduleData = ref({
      date: '',
      time: '',
      notes: ''
    })

    const minDate = computed(() => {
      return new Date().toISOString().split('T')[0]
    })

    const isFormValid = computed(() => {
      return scheduleData.value.date && scheduleData.value.time
    })

    const formatDate = (date) => {
      if (!date) return ''
      return new Date(date).toLocaleDateString('pt-BR')
    }

    const loadAvailableTimes = async () => {
      try {
        if (!scheduleData.value.date) {
          availableTimes.value = [];
          return;
        }

        isLoadingTimes.value = true;

        // Buscar horários disponíveis da API usando o endpoint específico da reconvocação
        const { recallQueueService } = await import('@/services/recallQueueService');
        const response = await recallQueueService.getRecallAvailableTimes(props.recallItem.id, scheduleData.value.date);
        
        if (response && response.data) {
          availableTimes.value = response.data;
        } else {
          availableTimes.value = [];
        }
      } catch (error) {
        console.error('Erro ao carregar horários:', error);
        availableTimes.value = [];
      } finally {
        isLoadingTimes.value = false;
      }
    }



    const handleSchedule = async () => {
      if (!isFormValid.value) return

      isProcessing.value = true

      try {
        const schedulePayload = {
          recall_id: props.recallItem.id,
          date: scheduleData.value.date,
          time: scheduleData.value.time,
          notes: scheduleData.value.notes
        }

        await recallQueueService.scheduleRecall(schedulePayload)
        
        emit('scheduled')
      } catch (error) {
        console.error('Erro ao agendar reconvocação:', error)
        // Aqui você pode adicionar uma notificação de erro
      } finally {
        isProcessing.value = false
      }
    }

    onMounted(() => {
      loadAvailableTimes()
      // Define a data mínima como hoje
      scheduleData.value.date = minDate.value
    })

    // Watcher para recarregar horários quando a data mudar
    watch(() => scheduleData.value.date, (newDate) => {
      if (newDate) {
        loadAvailableTimes()
      } else {
        availableTimes.value = []
      }
    })

    return {
      isProcessing,
      scheduleData,
      availableTimes,
      minDate,
      isFormValid,
      formatDate,
      handleSchedule,
      isLoadingTimes
    }
  }
}
</script> 